import React, { useMemo } from 'react';
import { Table, Typography } from 'antd';
import { WarehouseBalanceRow, WarehouseLiquidCard, WarehousePackageRow } from '../../api/warehouse';
import CardSection from './CardSection';
import { packageColumns } from './packageColumns';
import PackageMarks from './PackageMarks';
import { num } from './format';

const { Text } = Typography;

interface StocksSectionProps {
  card: WarehouseLiquidCard;
}

const balanceColumns = [
  { title: 'Ед.', dataIndex: 'unit', key: 'unit', width: 50 },
  {
    title: 'Резерв', dataIndex: 'to_ship', key: 'to_ship', align: 'right' as const, width: 80,
    render: (v: number) => v > 0 ? <span style={{ color: '#cf1322' }}>{num(v)}</span> : null,
  },
  {
    title: 'Доступно', dataIndex: 'available', key: 'available', align: 'right' as const, width: 90,
    render: (v: number) => (
      <span style={{ fontWeight: 600, color: Number(v) <= 0 ? '#cf1322' : undefined }}>{num(v)}</span>
    ),
  },
];

const warehouseSubhead = (text: string, first = false) => (
  <Text
    type="secondary"
    style={{ display: 'block', fontSize: 12, margin: first ? '0 0 8px' : '12px 0 8px' }}
  >
    {text}
  </Text>
);

const StocksSection: React.FC<StocksSectionProps> = ({ card }) => {
  // Rev. 19: «Основной» — рабочий склад (warehouse_id из ответа карточки);
  // прочие склады — отдельными подблоками, только позиции в наличии.
  const mainBalances = useMemo(
    () => card.balances.filter(b => b.warehouse_id === card.warehouse_id),
    [card],
  );
  const otherWarehouses = useMemo(() => {
    const result: { id: string; name: string; rows: WarehouseBalanceRow[] }[] = [];
    for (const b of card.balances) {
      if (b.warehouse_id === card.warehouse_id || b.available <= 0) continue;
      let w = result.find(x => x.id === b.warehouse_id);
      if (!w) {
        w = { id: b.warehouse_id, name: b.warehouse_name, rows: [] };
        result.push(w);
      }
      w.rows.push(b);
    }
    return result;
  }, [card]);

  return (
    <CardSection title="Остатки на складах">
      <div className="product-card-stocks">
        {warehouseSubhead('Основной', true)}
        {card.packages.length > 0 ? (
          <>
            <Table
              dataSource={card.packages}
              columns={packageColumns(false, false)}
              rowKey="item_id"
              size="small"
              pagination={false}
              scroll={{ x: 'max-content' }}
              rowClassName={(p: WarehousePackageRow) => p.inactive ? 'warehouse-package-inactive' : ''}
              expandable={{
                expandRowByClick: true,
                expandedRowRender: (p: WarehousePackageRow) => <PackageMarks itemId={p.item_id} />,
              }}
            />
            {card.packages_hidden > 0 && (
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                Скрыто старых позиций 1С: {card.packages_hidden}
              </Text>
            )}
          </>
        ) : (
          <Table
            dataSource={mainBalances}
            rowKey={b => `${b.warehouse_id}:${b.unit}`}
            size="small"
            pagination={false}
            columns={balanceColumns}
          />
        )}
        {otherWarehouses.map(w => (
          <div key={w.id}>
            {warehouseSubhead(w.name)}
            <Table
              dataSource={w.rows}
              rowKey={b => `${b.warehouse_id}:${b.unit}`}
              size="small"
              pagination={false}
              columns={balanceColumns}
            />
          </div>
        ))}
      </div>
    </CardSection>
  );
};

export default StocksSection;
